/**
 * !! What exactly is "pre-operation" data? !!
 *
 * pre-operations data refers to the operations data that
 * has not yet been made aware of or infused with the current operation.
 *
 * These hooks are ONLY to be used in places in the Core where
 * operations data is required before the current operation is determined.
 *
 * The general dependency graph looks like:
 *
 * preOperations -> currentOperationId -> currentOperation
 *       \                                       /
 *        –––––––––––> operations <––––––––––––––
 *
 */

import { useMemo } from "react";
import { AxiosError } from "axios";

import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
  ensureState,
  ensureStateWithData,
} from "../../../../services/query";
import { OperationsDataEnvelope } from "../../../../models/operations";

import { usePreOperationsQuery } from "./preOperations.query";

export * from "./preOperations.invalidate";

export const operationsAccessError = new Error(
  "Attempted to access operations data but it is not available",
);

// ############################
// ## usePreOperationsStatus ##
// ############################

export type ResultOfUsePreOperationsStatus = QueryStatus;
export type UsePreOperationsStatus = () => ResultOfUsePreOperationsStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of the pre-operations data.
 */
export const usePreOperationsStatus: UsePreOperationsStatus = () => {
  return usePreOperationsQuery({ watch: ["status"] }).status;
};

// ###########################
// ## usePreOperationsError ##
// ###########################

export type ResultOfUsePreOperationsError = AxiosError | null;
export type UsePreOperationsError = () => ResultOfUsePreOperationsError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of the pre-operations data
 * or null if no error occurred.
 */
export const usePreOperationsError: UsePreOperationsError = () => {
  return usePreOperationsQuery({ watch: ["error"] }).error;
};

// ###############################
// ## useMaybePreOperationsData ##
// ###############################

export type ResultOfUseMaybePreOperationsData =
  | OperationsDataEnvelope
  | undefined;
export type UseMaybePreOperationsData = () => ResultOfUseMaybePreOperationsData;

/**
 * The returned value of this hook will contain
 * the pre-operations data, which may be undefined
 */
export const useMaybePreOperationsData = () => {
  return usePreOperationsQuery({ watch: ["data"] }).data;
};

// ##########################
// ## usePreOperationsData ##
// ##########################

export type ResultOfUsePreOperationsData = OperationsDataEnvelope;
export type UsePreOperationsData = () => ResultOfUsePreOperationsData;

/**
 * The returned value of this hook will contain the pre-operations data.
 *
 * This hook is guaranteed to contain pre-operations data or will throw.
 */
export const usePreOperationsData: UsePreOperationsData = () => {
  const data = useMaybePreOperationsData();
  if (data === undefined) {
    throw operationsAccessError;
  }
  return data;
};

// ###########################
// ## useMaybePreOperations ##
// ###########################

export type ResultOfUseMaybePreOperations =
  QueryEnvelope<OperationsDataEnvelope>;
export type UseMaybePreOperations = () => ResultOfUseMaybePreOperations;

/**
 * The `data` property returned from this hook will contain
 * the pre-operations data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the pre-operations data.
 */
export const useMaybePreOperations: UseMaybePreOperations = () => {
  const { status, error, data } = usePreOperationsQuery({
    watch: ["status", "error", "data"],
  });
  return useMemo(
    () => ensureState({ status, error, data }),
    [status, error, data],
  );
};

// ######################
// ## usePreOperations ##
// ######################

export type ResultOfUsePreOperations =
  EnsuredQueryEnvelope<OperationsDataEnvelope>;
export type UsePreOperations = () => ResultOfUsePreOperations;

/**
 * The `data` property returned from this hook will contain
 * the pre-operations data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the pre-operations data.
 *
 * This hook is guaranteed to contain pre-operations data or will throw.
 */
export const usePreOperations: UsePreOperations = () => {
  const { status, error, data } = usePreOperationsQuery({
    watch: ["status", "error", "data"],
  });
  if (data === undefined) {
    throw operationsAccessError;
  }
  return useMemo(
    () => ensureStateWithData({ status, error, data }),
    [status, error, data],
  );
};
